import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { clone, groupBy } from 'lodash';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { env } from '../../../environments/environment';
import { DialogService } from '../../dialog/dialog.service';
import { SectionService } from '../../section/section.service';
import { SettingService } from '../../setting/setting.service';
import {
  FlagGroupTag,
  InspectionVersion,
  InvalidQuestion,
  Question,
  QuestionTag,
  Section,
} from '../../shared/interfaces';
import { permissions } from '../../shared/permissions';
import { getCurrentVersionName, setCurrentVersionName } from '../../shared/version.name.helper';
import { UserService } from '../../user/user.service';
import { VersionService } from '../../version/version.service';
import { FlagGroupTagService } from '../flag.group.tag.service';
import { QuestionService } from '../question.service';
import { QuestionTagService } from '../question.tag.service';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { ItemService } from '../../item/item.service';

@Component({
  selector: 'app-question-tree',
  templateUrl: './question-tree.component.html',
  styleUrls: ['./question-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class QuestionTreeComponent implements OnInit, AfterViewInit {
  get selectedQuestion() {
    return this._selectedQuestion;
  }
  set selectedQuestion(question: Question) {
    this._selectedQuestion = question;
    if (question) {
      this._selectedSection = this.allSections.find(s => s.id === (question.section && question.section.id));
      this.questionSubject.next(question);
      this.waitAndScrollToSelectedQuestion();
      this.questionService.getFlagGroupTags().then(flagGroupTags => {
        this.flagGroupTags = flagGroupTags;
      });
      this.questionService.getQuestionTags().then(questionTags => {
        this.questionTags = questionTags;
      });
    }
  }

  get selectedSection() {
    return this._selectedSection;
  }
  set selectedSection(section: Section) {
    // If we're loading data with a selectedId, don't auto-navigate to first question
    const hasSelectedIdFromRoute = !!this.selectedId;

    if (
      section &&
      (section.id !== (this.selectedSection && this.selectedSection.id) ||
        section.version !== (this.selectedSection && this.selectedSection.version))
    ) {
      this.Qloading = true;
      this.questions = null;

      // Don't clear selectedQuestion if we have a selectedId from route
      if (!hasSelectedIdFromRoute) {
        this.selectedQuestion = null;
      }

      this.sectionService.questions(section.id, this.versionName).subscribe(x => {
        this.questions = x;

        // Only auto-select first question if we don't have a selectedId from route
        if (!hasSelectedIdFromRoute) {
          this._selectedQuestion = this.questions[0];
          if (this._selectedQuestion) {
            this.router.navigate(['question', 'tree', this._selectedQuestion.id], {
              queryParams: { versionName: this.versionName },
            });
          }
        } else {
          // If we have selectedId, find that specific question
          this._selectedQuestion = this.questions.find(q => q.id === this.selectedId);
        }

        this.Qloading = false;
      });
    }

    this._selectedSection = section;
  }

  get versionName() {
    return this._versionName;
  }
  set versionName(name) {
    if (this._versionName) {
      // changing version, new set of questions
      // this.selectedSection = null;
      this.selectedQuestion = null;
    }
    this._versionName = name;
    this.router.navigate([location.pathname], { queryParams: { versionName: this.versionName } });
    setCurrentVersionName(name);
    this.loadDataSubject.next();
  }

  constructor(
    public route: ActivatedRoute,
    public sectionService: SectionService,
    public questionService: QuestionService,
    public userService: UserService,
    public fb: UntypedFormBuilder,
    private toastr: ToastrService,
    public router: Router,
    private dialog: DialogService,
    public versionService: VersionService,
    public settingService: SettingService,
    public flagGroupTagService: FlagGroupTagService,
    public questionTagService: QuestionTagService,
    public itemService: ItemService,
    private cdRef: ChangeDetectorRef,
  ) {
    this.questionSubject.pipe(debounceTime(300)).subscribe((question: Question) => {
      this.setData(question);
    });

    this.loadDataSubject.pipe(debounceTime(300)).subscribe(() => {
      this.loadData();
    });
  }

  versions;

  _versionName = '';

  @ViewChild('wrapper', { static: true }) private wrapper: ElementRef;

  @ViewChild('optionModal', { static: true }) public optionModal: ModalDirective;
  editingOption: UntypedFormGroup;
  @ViewChild('validationModal', { static: true }) public validationModal: ModalDirective;
  validationQuestions = [];

  form: UntypedFormGroup;

  sections: Section[] = [];
  questions: Question[] = [];
  flagGroupTags: FlagGroupTag[] = [];
  questionTags: QuestionTag[] = [];
  pickQuestionsAdmin: Question[] = [];
  pickQuestionsInspector: Question[] = [];
  pickQuestionsPicture: Question[] = [];
  pickQuestionsNotes: Question[] = [];
  pickQuestionsPosition: Question[] = [];
  pickQuestionsRelated: Question[] = [];
  pickSections: any[] = [];
  pickSectionsRelated: any[] = [];

  newRelatedQuestion: Question;

  canEdit = false;
  canEditHelp = false;
  scoresEnabled = true;
  helpPicturesloading = false;
  _selectedQuestion: Question;

  _selectedSection: Section;

  originalOption;
  fileToUpload: File;

  selectedId;
  questionFilter = 'all';
  items;
  repeatOptions;
  version: InspectionVersion;
  invalidQuestions: InvalidQuestion[];
  Qloading = false;
  private questionSubject: Subject<Question> = new Subject<Question>();
  private loadDataSubject: Subject<void> = new Subject<void>(); // New subject for loadData

  ngAfterViewInit() {
    this.optionModal.config.backdrop = 'static';
  }

  async loadPickQuestions() {
    const questions = await this.questionService.pick(this.versionName);
    this.pickSections = questions
      .filter(q => q.type === 'pick-list')
      .reduce((acc, question) => {
        const section = acc.find(x => question.section && x.id === question.section.id);
        if (section) {
          section.questions.push(question);
          return acc;
        } else {
          return [
            ...acc,
            {
              ...question.section,
              questions: [question],
            },
          ];
        }
      }, []);

    this.pickSectionsRelated = questions.reduce((acc, question) => {
      const section = acc.find(x => question.section && x.id === question.section.id);
      if (section) {
        section.questions.push(question);
        return acc;
      } else {
        return [
          ...acc,
          {
            ...question.section,
            questions: [question],
          },
        ];
      }
    }, []);
  }

  allSectionsByItem = [];
  allSections = [];

  async fetchAllSections() {
    const all = (await this.sectionService.list({ pageSize: 999, versionName: this.versionName }).toPromise())[0];
    this.allSections = all;
    this.allSectionsByItem = [];
    for (const section of all) {
      const item = section.item;

      const existing = this.allSectionsByItem.find(i => i.find(s => s.item.id === item.id));

      if (!existing) {
        const newItem = [section];
        (newItem as any).order = section.item.order;
        this.allSectionsByItem.push(newItem);
        continue;
      }
      existing.push(section);
    }

    return this.allSectionsByItem.sort((a, b) => a.order - b.order);
  }
  async ngOnInit() {
    this.form = this.fb.group({
      active: [true, []],
      text: ['', [Validators.required]],
      helpText: '',
      helpPictures: this.fb.array([]),
      type: ['', []],
      options: this.fb.array([]),
      section: ['', [Validators.required]],
      legacyId: ['', []],
      adminVisibilitySection: ['', this.validateConditionalSection.bind(this)],
      adminVisibility: ['', [Validators.required]],
      adminVisibilityQuestion: ['', []],
      adminVisibilityAnswer: ['', []],
      inspectorVisibility: ['', [Validators.required]],
      inspectorVisibilitySection: ['', this.validateConditionalSection.bind(this)],
      inspectorVisibilityQuestion: ['', []],
      inspectorVisibilityAnswer: ['', []],
      pictureRequired: ['', [Validators.required]],
      pictureRequiredSection: ['', this.validateConditionalSection.bind(this)],
      pictureRequiredQuestion: '',
      pictureRequiredAnswer: '',
      pictureOrientation: 'landscape',
      pictureGuidanceText: '',
      notesRequired: ['', [Validators.required]],
      notesRequiredSection: ['', this.validateConditionalSection.bind(this)],
      notesRequiredQuestion: '',
      notesRequiredAnswer: '',
      locationRequired: ['', [Validators.required]],
      locationRequiredSection: ['', this.validateConditionalSection.bind(this)],
      locationRequiredQuestion: '',
      locationRequiredAnswer: '',
      repeat: [],
      showRelated: [false, []],
      newRelatedSection: '',
      mandatoryReview: [true, []],
      flagGroupTags: [],
      questionTags: [],
    });

    this.form.get('flagGroupTags').valueChanges.subscribe(value => {
      this.onTagsChange(value);
    });

    this.form.get('questionTags').valueChanges.subscribe(value => {
      this.onQuestionTagsChange(value);
    });

    this.versions = await this.versionService.list();
    this.versions = this.versions.map(v => ({
      ...v,
      label: !v.isValid ? `${v.name} (invalid)` : v.name,
    }));

    await new Promise<void>(res => {
      this.route.queryParamMap.subscribe(async queryParamMap => {
        const queryVersion = queryParamMap.get('versionName');

        // Set versionName from query param or use stored/default
        if (queryVersion) {
          this.versionName = queryVersion;
        } else if (!this.versionName) {
          const storedVersion = getCurrentVersionName();
          this.versionName = storedVersion || this.versions[0].name;
        }

        this.sections = await this.fetchAllSections();

        this.route.paramMap.subscribe(async params => {
          // Get the question ID from route
          this.selectedId = +params.get('id');

          // Only update versionName if there's a query param
          // Don't override with null/undefined
          if (queryVersion) {
            this.versionName = queryVersion;
          }
          if (!this.selectedId) {
            this.route.queryParams.subscribe(queryString => {
              const sectionId = parseInt(queryString['section'], 10);
              const section = this.sections.find(x => x.id === sectionId);

              if (section) {
                this.selectedSection = section;
                setTimeout(() => this.addQuestion(), 1);
              } else {
                this.selectedSection = null;
                this.selectedQuestion = null;
              }
            });
          }
          res();
          this.loadDataSubject.next();
        });
      });
    });

    this.scoresEnabled = await this.settingService.scoresEnabled();
    this.canEdit = await this.userService.hasPermissions([permissions.QUESTION.WRITE]);
    this.canEditHelp = await this.userService.hasPermissions([permissions.QUESTION.MANAGE_HELP]);

    if (!this.canEdit) {
      this.form.disable();
      this.form.get('helpText').enable();
      this.form.get('helpPictures').enable();
    }

    if (!this.canEditHelp) {
      console.log('trying to disable');
      this.form.get('helpText').disable();
      this.form.get('helpPictures').disable();
    }

    this.form.get('newRelatedSection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsRelated = x.questions;
        this.newRelatedQuestion = this.pickQuestionsRelated[0];
      } else {
        this.pickQuestionsRelated = [];
        this.newRelatedQuestion = undefined;
      }
    });

    this.form.get('newRelatedSection').setValue(this.pickSections[0]);

    this.form.get('adminVisibilitySection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsAdmin = x.questions.filter(q => q.id !== this.selectedQuestion.id);
      } else {
        this.pickQuestionsAdmin = [];
      }
      this.form.get('adminVisibilityQuestion').setValue(this.pickQuestionsAdmin[0]);
    });

    this.form.get('adminVisibilityQuestion').valueChanges.subscribe(x => {
      this.form.get('adminVisibilityAnswer').setValue(x ? x.options[0].text : '');
      this.cdRef.detectChanges();
    });

    this.form.get('inspectorVisibilitySection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsInspector = x.questions.filter(q => q.id !== this.selectedQuestion.id);
      } else {
        this.pickQuestionsInspector = [];
      }
      this.form.get('inspectorVisibilityQuestion').setValue(this.pickQuestionsInspector[0]);
    });

    this.form.get('inspectorVisibilityQuestion').valueChanges.subscribe(x => {
      this.form.get('inspectorVisibilityAnswer').setValue(x ? x.options[0].text : '');
      this.cdRef.detectChanges();
    });

    this.form.get('inspectorVisibility').valueChanges.subscribe(val => {
      if (val === 'never') {
        this.form.get('mandatoryReview').setValue(true);
      }
    });

    this.form.get('pictureRequiredSection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsPicture = x.questions;
      } else {
        this.pickQuestionsPicture = [];
      }
      this.form.get('pictureRequiredQuestion').setValue(this.pickQuestionsPicture[0]);
    });

    this.form.get('pictureRequiredQuestion').valueChanges.subscribe(x => {
      this.form.get('pictureRequiredAnswer').setValue(x ? x.options[0].text : '');
      this.cdRef.detectChanges();
    });

    this.form.get('notesRequiredSection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsNotes = x.questions;
      } else {
        this.pickQuestionsNotes = [];
      }
      this.form.get('notesRequiredQuestion').setValue(this.pickQuestionsNotes[0]);
    });

    this.form.get('notesRequiredQuestion').valueChanges.subscribe(x => {
      this.form.get('notesRequiredAnswer').setValue(x ? x.options[0].text : '');
      this.cdRef.detectChanges();
    });

    this.form.get('locationRequiredSection').valueChanges.subscribe(x => {
      if (x) {
        this.pickQuestionsPosition = x.questions;
      } else {
        this.pickQuestionsPosition = [];
      }
      this.form.get('locationRequiredQuestion').setValue(this.pickQuestionsPosition[0]);
    });

    this.form.get('locationRequiredQuestion').valueChanges.subscribe(x => {
      this.form.get('locationRequiredAnswer').setValue(x ? x.options[0].text : '');
      this.cdRef.detectChanges();
    });

    this.form.get('type').valueChanges.subscribe(value => {
      if (value === 'pick-list') {
        this.form.get('options').setValidators([Validators.required, Validators.minLength(1)]);
        this.form.get('options').updateValueAndValidity();
      } else {
        this.form.get('options').clearValidators();
        this.form.get('options').updateValueAndValidity();
      }
    });
  }

  async loadData() {
    this.loadPickQuestions();
    this.version = await this.versionService.latest(this.versionName);
    this.invalidQuestions = !this.version.isValid ? await this.versionService.checkTree(this.version.id) : [];
    this.items = (await this.versionService.tree(this.version.id)).items;

    if (this.selectedId) {
      this.selectedQuestion = await this.questionService.get(this.selectedId, this.versionName);
      if (!this.selectedQuestion) {
        this.router.navigate(['question', 'tree'], { queryParams: { versionName: this.versionName } });
        return;
      }
      this.questions = await this.sectionService
        .questions(this.selectedQuestion.section.id, this.versionName)
        .toPromise();

      // Clear selectedId after successfully loading the question
      this.selectedId = null;
    } else if (!this.selectedSection) {
      this.selectedSection = this.sections[0][0];
    }
    this.cdRef.markForCheck();
  }

  selectSetion(item) {
    this.selectedSection = item;
    this.cdRef.markForCheck();
  }

  setData(data: Question) {
    this.newRelatedQuestion = undefined;

    const options = this.form.controls.options as UntypedFormArray;

    this.repeatOptions = this.items.map(item => {
      return {
        name: item.name,
        id: item.id,
      };
    });

    console.log('data', this.repeatOptions);

    this.form.patchValue({
      ...data,
      section: data.section && data.section.id,
      options: [],
      helpPictures: [],
    });

    this.form.get('repeat').setValue(data.repeat[0]);

    while (options.length > 0) {
      this.removeOption(0);
    }

    if (data.options) {
      data.options.forEach(x => {
        options.push(this.optionAsGroup(x));
      });
    }

    const helpPictures = this.form.controls.helpPictures as UntypedFormArray;
    while (helpPictures.length > 0) {
      helpPictures.removeAt(0);
    }

    if (data.helpPictures) {
      data.helpPictures.forEach(hp => {
        helpPictures.push(
          this.fb.group({
            caption: '',
            ...hp,
          }),
        );
      });
    }

    let adminVisibilityQuestion = null;

    const adminVisibilitySection = this.pickSections.find(s => {
      const found = s.questions.find(q => q.id === data.adminVisibilityQuestionId);
      if (found) {
        adminVisibilityQuestion = found;
      }
      return !!found;
    });

    this.form.get('adminVisibilitySection').setValue(adminVisibilitySection);
    this.form.get('adminVisibilityQuestion').setValue(adminVisibilityQuestion);
    this.form.get('adminVisibilityAnswer').setValue(data.adminVisibilityAnswer);

    let inspectorVisibilityQuestion = null;

    const inspectorVisibilitySection = this.pickSections.find(s => {
      const found = s.questions.find(q => q.id === data.inspectorVisibilityQuestionId);
      if (found) {
        inspectorVisibilityQuestion = found;
      }
      return !!found;
    });

    this.form.get('inspectorVisibilitySection').setValue(inspectorVisibilitySection);
    this.form.get('inspectorVisibilityQuestion').setValue(inspectorVisibilityQuestion);
    this.form.get('inspectorVisibilityAnswer').setValue(data.inspectorVisibilityAnswer);

    let pictureRequiredQuestion = null;

    const pictureRequiredSection = this.pickSections.find(s => {
      const found = s.questions.find(q => q.id === data.pictureRequiredQuestionId);
      if (found) {
        pictureRequiredQuestion = found;
      }
      return !!found;
    });

    this.form.get('pictureRequiredSection').setValue(pictureRequiredSection);
    this.form.get('pictureRequiredQuestion').setValue(pictureRequiredQuestion);
    this.form.get('pictureRequiredAnswer').setValue(data.pictureRequiredAnswer);

    let notesRequiredQuestion = null;

    const notesRequiredSection = this.pickSections.find(s => {
      const found = s.questions.find(q => q.id === data.notesRequiredQuestionId);
      if (found) {
        notesRequiredQuestion = found;
      }
      return !!found;
    });

    this.form.get('notesRequiredSection').setValue(notesRequiredSection);
    this.form.get('notesRequiredQuestion').setValue(notesRequiredQuestion);
    this.form.get('notesRequiredAnswer').setValue(data.notesRequiredAnswer);

    let locationRequiredQuestion = null;

    const locationRequiredSection = this.pickSections.find(s => {
      const found = s.questions.find(q => q.id === data.locationRequiredQuestionId);
      if (found) {
        locationRequiredQuestion = found;
      }
      return !!found;
    });

    this.form.get('locationRequiredSection').setValue(locationRequiredSection);
    this.form.get('locationRequiredQuestion').setValue(locationRequiredQuestion);
    this.form.get('locationRequiredAnswer').setValue(data.locationRequiredAnswer);

    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.cdRef.markForCheck();
  }

  optionAsGroup(option) {
    const group = this.fb.group({
      text: '',
      helpText: '',
      flaggingScore: 0,
      flaggingComment: 'Automatically created as per question configuration',
      ...option,
      helpPictures: this.fb.array(
        (option.helpPictures || []).map(hp =>
          this.fb.group({
            caption: '',
            Location: '',
            ...hp,
          }),
        ),
      ),
    });

    if (!this.canEditHelp) {
      group.get('helpText').disable();
      group.get('helpPictures').disable();
    }
    if (!this.canEdit) {
      group.get('text').disable();
    }

    return group;
  }

  getValidationClass(field: string) {
    const control = this.form.get(field);
    return {
      'is-valid': control.valid && (control.dirty || control.touched),
      'is-invalid': control.invalid && (control.dirty || control.touched),
    };
  }

  validateConditionalSection(control: UntypedFormControl) {
    if (!control.value) {
      return null;
    }

    const inspectionItem = control.value.inspectionItems?.[0];

    if (inspectionItem && inspectionItem.minInstances === 0 && inspectionItem.name !== this.selectedSection.item.name) {
      return { minInstances: true };
    }
  }

  async onTagsChangeGeneric(
    changedTags: (FlagGroupTag | QuestionTag | string)[],
    tagType: 'flagGroupTags' | 'questionTags',
    tagService: FlagGroupTagService | QuestionTagService,
  ) {
    const copyChangedTags = clone(changedTags);
    changedTags.forEach((item, index) => {
      const exists = changedTags
        .filter((x, i) => {
          const itemName = typeof item === 'string' ? item : item.name;
          const xName = typeof x === 'string' ? x : x.name;
          return xName.toLowerCase() === itemName.toLowerCase();
        })
        .map(x => changedTags.indexOf(x));
      if (exists.length > 1) {
        const removeIndex = exists[1];
        changedTags.splice(removeIndex, 1);
      }
    });
    if (copyChangedTags.length !== changedTags.length) {
      this.form.get(tagType).setValue(changedTags);
    }

    for (const [i, item] of changedTags.entries()) {
      if (typeof item === 'string') {
        if (this[tagType].find(x => x.name.toLowerCase() === item.toLowerCase())) {
          changedTags[i] = this[tagType].find(x => x.name.toLowerCase() === item.toLowerCase());
        } else {
          const tagResponse = await tagService.create({ name: item });
          this[tagType].push(tagResponse);
          changedTags[i] = tagResponse;
        }
        this.form.get(tagType).setValue(changedTags);
      }
    }
  }
  async onTagsChange(changedTags: (FlagGroupTag | string)[]) {
    await this.onTagsChangeGeneric(changedTags, 'flagGroupTags', this.flagGroupTagService);
  }

  async onQuestionTagsChange(changedTags: (QuestionTag | string)[]) {
    await this.onTagsChangeGeneric(changedTags, 'questionTags', this.questionTagService);
  }

  async submit() {
    for (const c of Object.keys(this.form.controls)) {
      this.form.controls[c].markAsTouched();
    }

    // if (!(await this.checkQuestionValadityMinInstance(this.form.getRawValue()))) {
    //   return false;
    // }

    const repeat = this.form.get('repeat').value ? [this.form.get('repeat').value] : [];

    if (this.form.valid) {
      const value = this.form.getRawValue();
      const dto = {
        ...value,
        section: { id: value.section },
        related: (this.selectedQuestion.related || []).map(q => q.id),
        repeat,
      };

      if (this.selectedQuestion.id) {
        try {
          const question = await this.questionService.edit(this.selectedQuestion.id, dto, this.versionName);
          this.loadPickQuestions();
          this.questionSubject.next(question);
          this.toastr.success('Question saved!');
          this.sections = await this.fetchAllSections();
          this.selectedQuestion = question;
        } catch (e) {
          console.log('error edit', e);
        }
      } else {
        try {
          const question = await this.questionService.create(dto, this.versionName);
          this.loadPickQuestions();
          this.router.navigate(['question', 'tree'], { queryParams: { versionName: this.versionName } });
          this.toastr.success('Question saved!');
          this.sections = await this.fetchAllSections();
          this.selectedQuestion = question;

          this.questions = await this.sectionService
            .questions(this.selectedQuestion.section.id, this.versionName)
            .toPromise();
        } catch (e) {
          console.log('error create', e);
        }
      }
      await this.loadDataSubject.next();
    }
  }

  async checkQuestionValadityMinInstance(question): Promise<boolean> {
    return await this.itemService.get(this.selectedSection.item.id, this.versionName).then(item => {
      if (
        !item.minInstances &&
        (question.inspectorVisibility === 'conditional' ||
          question.adminVisibility === 'conditional' ||
          question.pictureRequired === 'conditional' ||
          question.locationRequired === 'conditional' ||
          question.notesRequired === 'conditional')
      ) {
        this.toastr.error(
          "The minimum quantity cannot be zero because there's conditional logic that depends on this item.",
        );
        return false;
      }
      return true;
    });
  }

  addQuestion() {
    this.selectedQuestion = {
      active: true,
      section: this.selectedSection,
      text: 'New question?',
      helpText: '',
      helpPictures: [],
      type: 'free-text',
      legacyId: '',
      adminVisibility: 'always',
      adminVisibilityQuestion: null,
      adminVisibilityAnswer: '',
      inspectorVisibility: 'always',
      inspectorVisibilityQuestion: null,
      inspectorVisibilityAnswer: '',
      pictureRequired: 'optional',
      pictureRequiredQuestion: null,
      pictureRequiredAnswer: '',
      pictureOrientation: 'landscape',
      pictureGuidanceText: '',
      notesRequired: 'optional',
      notesRequiredQuestion: null,
      notesRequiredAnswer: '',
      locationRequired: 'optional',
      locationRequiredQuestion: null,
      locationRequiredAnswer: '',
      repeat: [],
    };
    this.questionSubject.next(this.selectedQuestion);
  }

  openSelectedSection() {
    this.router.navigate(['section', 'form', this.selectedSection.id]);
  }
  openSelectedItem() {
    this.router.navigate(['item', 'form', this.selectedSection.item.id]);
  }

  archive() {
    this.dialog.confirm().show({
      message: `Do you really want to delete this Question?`,
      confirmed: async () => {
        try {
          await this.questionService.archive(this.selectedQuestion.id, this.versionName);

          this.toastr.success('Section updated!');
          const questionIndex = this.questions.findIndex(q => q.id === this.selectedQuestion.id);
          this.questions.splice(questionIndex, 1);
          if (this.questions.length > 0) {
            const nextQuestionIndex = Math.min(questionIndex, this.questions.length - 1);
            this.selectedQuestion = this.questions[nextQuestionIndex];
          } else {
            this.selectedQuestion = null;
          }
          this.sectionService.questions(this.selectedSection.id, this.versionName).subscribe(x => {
            this.questions = x;
          });
          await this.loadDataSubject.next();
        } catch (e) {
          const error = e.error;
          if (error.errors && error.errors.length) {
            const groupedByQuestionText = groupBy(error.errors, 'originalId');
            this.validationQuestions = Object.values(groupedByQuestionText).map(questionsWithSameText => {
              return {
                originalId: questionsWithSameText[0].originalId,
                questionText: questionsWithSameText[0].questionText,
                type: questionsWithSameText.map(q => q.type).join(', '),
              };
            });
            this.validationModal.show();
            this.cdRef.detectChanges();
          }
        }
      },
    });
  }

  ngOnInitView() {}

  getItemName(section: Section) {
    if (!section || !section.inspectionItems || !section.inspectionItems.length) {
      return 'Item not found!';
    }

    return section.inspectionItems[0].name;
  }

  waitAndScrollToSelectedQuestion() {
    setTimeout(() => {
      const li: any = document.getElementsByClassName('questionActive')[0];
      if (li) {
        li.scrollIntoViewIfNeeded ? li.scrollIntoViewIfNeeded() : li.scrollIntoView();
      } else {
        this.waitAndScrollToSelectedQuestion();
      }
    }, 20);
  }

  getOptions() {
    return (this.form.controls.options as UntypedFormArray).controls;
  }

  removeOption(i) {
    (this.form.controls.options as UntypedFormArray).removeAt(i);
  }

  addOption() {
    (this.form.controls.options as UntypedFormArray).push(
      this.optionAsGroup({
        text: 'New Option',
        helpText: '',
        helpPictures: [],
      }),
    );
  }

  openOption(opt) {
    this.originalOption = opt.getRawValue();
    this.editingOption = opt;
    this.optionModal.show();
  }

  closeOption() {
    this.optionModal.hide();
  }

  cancelOption() {
    this.optionModal.hide();
    this.editingOption.setValue(this.originalOption);
  }

  setUploadFile(files: FileList) {
    this.fileToUpload = files.item(0);
  }

  async uploadFile(input: HTMLInputElement) {
    this.helpPicturesloading = true;
    const formData = new FormData();
    formData.append('upload', this.fileToUpload, this.fileToUpload.name);
    const uploadInfo = await this.questionService.uploadPicture(formData);
    (this.form.get('helpPictures') as UntypedFormArray).push(
      this.fb.group({
        ...uploadInfo,
        caption: '',
      }),
    );
    input.value = null;
    this.helpPicturesloading = false;
    this.cdRef.detectChanges();
  }

  async uploadOptionFile(input: HTMLInputElement) {
    const formData = new FormData();
    formData.append('upload', this.fileToUpload, this.fileToUpload.name);
    const uploadInfo = await this.questionService.uploadPicture(formData);
    (this.editingOption.get('helpPictures') as UntypedFormArray).push(
      this.fb.group({
        ...uploadInfo,
        caption: '',
      }),
    );
    input.value = null;
    this.cdRef.detectChanges();
  }

  getSrc(img) {
    return `${env.APIUrl}/image/download/${img.key}`;
  }

  removeImage(i) {
    (this.form.get('helpPictures') as UntypedFormArray).removeAt(i);
  }

  removeOptionImage(i) {
    (this.editingOption.get('helpPictures') as UntypedFormArray).removeAt(i);
  }

  hasAllHelp() {
    const q = this.form.value;

    if (!q) {
      return false;
    }

    if (!q.helpText) {
      return false;
    }

    if (q.type === 'pick-list' && q.options && Array.isArray(q.options)) {
      for (const o of q.options) {
        if (!o.helpText) {
          return false;
        }
      }
    }
    return true;
  }

  addRelated() {
    if (!Array.isArray(this.selectedQuestion.related)) {
      this.selectedQuestion.related = [];
    }

    if (!this.selectedQuestion.related.find(q => q.id === this.newRelatedQuestion.id)) {
      this.selectedQuestion.related.push(this.newRelatedQuestion);
    } else {
      this.toastr.error('Question already added!');
    }
  }

  removeRelated(index) {
    this.selectedQuestion.related.splice(index, 1);
  }

  showQuestion(question: Question) {
    const isMandatory = question.mandatoryReview;
    const isScored =
      question.type === 'pick-list' &&
      question.options &&
      question.options.reduce((res, opt) => res || !!opt.flaggingScore, false);

    switch (this.questionFilter) {
      case 'mandatory':
        return isMandatory;
      case 'score':
        return isScored;
      case 'mandatoryOrScored':
        return isMandatory || isScored;
      case 'autoScored':
        return isScored;
    }

    return true;
  }

  dropSection(event: CdkDragDrop<string[]>) {
    const {
      previousIndex,
      currentIndex,
      item: {
        data: { section, sections },
      },
    } = event;
    console.log({ previousIndex, currentIndex, section, sections });

    if (previousIndex !== currentIndex) {
      moveItemInArray(sections, previousIndex, currentIndex);

      this.sectionService.move(section.item, section.id, currentIndex, this.versionName).then(newItemId => {
        this.toastr.success('Order updated!');
      });
    }
  }

  dropQuestion(event: CdkDragDrop<string[]>) {
    const {
      previousIndex,
      currentIndex,
      item: {
        data: { question, questions },
      },
    } = event;
    console.log({ previousIndex, currentIndex, question, questions });

    if (previousIndex !== currentIndex) {
      moveItemInArray(questions, previousIndex, currentIndex);

      this.questionService.move(question.section, question.id, currentIndex, this.versionName).then(newItemId => {
        this.toastr.success('Order updated!');
      });
    }
  }

  selectedTest() {
    return this.versions?.find(x => x.name === this.versionName);
  }

  isInvalidSection(sectionId: number) {
    return this.invalidQuestions?.find(x => x.sectionId === sectionId);
  }

  isInvalidQuestion(questionId: number) {
    return this.invalidQuestions?.find(x => x.originalId === questionId);
  }

  isInvalidQuestionType(
    type: 'Admin Visibility' | 'Inspector Visibility' | 'Location Required' | 'Notes Required' | 'Picture Required',
  ) {
    let requiredId = null;
    if (type === 'Admin Visibility') {
      requiredId = this.selectedQuestion.adminVisibilityQuestionId;
    } else if (type === 'Inspector Visibility') {
      requiredId = this.selectedQuestion.inspectorVisibilityQuestionId;
    } else if (type === 'Location Required') {
      requiredId = this.selectedQuestion.locationRequiredQuestionId;
    } else if (type === 'Notes Required') {
      requiredId = this.selectedQuestion.notesRequiredQuestionId;
    } else if (type === 'Picture Required') {
      requiredId = this.selectedQuestion.pictureRequiredQuestionId;
    }
    if (requiredId) {
      return this.invalidQuestions?.find(
        x => x.requiredId === requiredId && x.type === type && x.sectionId === this.selectedQuestion.section.id,
      );
    }
    return false;
  }

  goToQuestion(questionId: number) {
    this.router.navigate(['question', 'tree', questionId], { queryParams: { versionName: this.versionName } });
  }
}
